import React, { memo } from 'react';
import { ACCIDENT_SCENARIOS } from './sketchScenarios';

// ========== COMPONENTA SCHIȚĂ ACCIDENT (SVG) - STIL PROFESIONAL/TEHNIC ==========
export const AccidentSketch = memo(({ data, width = 380, height = 280, showLegend = true }) => {
  if (!data) return null;
  
  const W = width;
  const H = height;
  const CX = W / 2;
  const CY = H / 2;
  
  // Verifică dacă avem scenariu predefinit sau date directe
  const scenario = data.scenarioId ? ACCIDENT_SCENARIOS[data.scenarioId] : null;
  const config = scenario || data;
  const tipDrum = config.tipDrum || 'intersectie';
  
  // Scala pentru a converti coordonatele în SVG
  const scale = Math.min(W, H) / 220;
  
  const getVehiclePos = (vehicleData) => ({
    x: CX + (vehicleData?.x || 0) * scale,
    y: CY + (vehicleData?.y || 0) * scale,
    rotation: vehicleData?.rotation || 0
  });
  
  const posA = getVehiclePos(config.vehiculA);
  const posB = getVehiclePos(config.vehiculB);
  
  const impactPos = config.impactPoint ? {
    x: CX + (config.impactPoint.x || 0) * scale,
    y: CY + (config.impactPoint.y || 0) * scale
  } : {
    x: (posA.x + posB.x) / 2,
    y: (posA.y + posB.y) / 2
  };

  // Componenta pentru mașină - stil tehnic minimalist
  const Car = ({ x, y, rotation, filled, label }) => (
    <g transform={`translate(${x}, ${y}) rotate(${rotation})`}>
      {/* Corpul mașinii */}
      <rect x="-12" y="-20" width="24" height="40" rx="3" 
        fill={filled ? "#e0e0e0" : "none"} 
        stroke="#000" strokeWidth="2"/>
      {/* Parbriz (față) */}
      <line x1="-8" y1="-10" x2="8" y2="-10" stroke="#000" strokeWidth="1.5"/>
      {/* Lunetă (spate) */}
      <line x1="-8" y1="10" x2="8" y2="10" stroke="#000" strokeWidth="1.5"/>
      {/* Săgeată direcție */}
      <line x1="0" y1="-20" x2="0" y2="-28" stroke="#000" strokeWidth="1.5"/>
      <polygon points="0,-32 -4,-26 4,-26" fill="#000" stroke="none"/>
      {/* Label */}
      <circle cx="0" cy="0" r="9" fill="#fff" stroke="#000" strokeWidth="1.5"/>
      <text x="0" y="4" textAnchor="middle" fill="#000" fontWeight="bold" fontSize="11" fontFamily="Arial, sans-serif">{label}</text>
    </g>
  );

  // Desenează drumul în funcție de tip - stil blueprint minimalist
  const renderRoad = () => {
    switch (tipDrum) {
      case 'intersectie':
        return (
          <g>
            {/* Drum vertical - margini */}
            <line x1={CX - 35} y1="0" x2={CX - 35} y2={CY - 35} stroke="#333" strokeWidth="2"/>
            <line x1={CX + 35} y1="0" x2={CX + 35} y2={CY - 35} stroke="#333" strokeWidth="2"/>
            <line x1={CX - 35} y1={CY + 35} x2={CX - 35} y2={H} stroke="#333" strokeWidth="2"/>
            <line x1={CX + 35} y1={CY + 35} x2={CX + 35} y2={H} stroke="#333" strokeWidth="2"/>
            {/* Drum orizontal - margini */}
            <line x1="0" y1={CY - 35} x2={CX - 35} y2={CY - 35} stroke="#333" strokeWidth="2"/>
            <line x1="0" y1={CY + 35} x2={CX - 35} y2={CY + 35} stroke="#333" strokeWidth="2"/>
            <line x1={CX + 35} y1={CY - 35} x2={W} y2={CY - 35} stroke="#333" strokeWidth="2"/>
            <line x1={CX + 35} y1={CY + 35} x2={W} y2={CY + 35} stroke="#333" strokeWidth="2"/>
            {/* Linii centrale întrerupte */}
            <line x1={CX} y1="0" x2={CX} y2={CY - 40} stroke="#666" strokeWidth="1.5" strokeDasharray="10,8"/>
            <line x1={CX} y1={CY + 40} x2={CX} y2={H} stroke="#666" strokeWidth="1.5" strokeDasharray="10,8"/>
            <line x1="0" y1={CY} x2={CX - 40} y2={CY} stroke="#666" strokeWidth="1.5" strokeDasharray="10,8"/>
            <line x1={CX + 40} y1={CY} x2={W} y2={CY} stroke="#666" strokeWidth="1.5" strokeDasharray="10,8"/>
          </g>
        );
        
      case 'drum_drept':
        return (
          <g>
            {/* Margini drum */}
            <line x1={CX - 45} y1="0" x2={CX - 45} y2={H} stroke="#333" strokeWidth="2"/>
            <line x1={CX + 45} y1="0" x2={CX + 45} y2={H} stroke="#333" strokeWidth="2"/>
            {/* Linie centrală întreruptă */}
            <line x1={CX} y1="0" x2={CX} y2={H} stroke="#666" strokeWidth="1.5" strokeDasharray="10,8"/>
          </g>
        );
        
      case 'sens_giratoriu':
        return (
          <g>
            {/* Cercul giratoriu exterior */}
            <circle cx={CX} cy={CY} r="80" fill="none" stroke="#333" strokeWidth="2"/>
            {/* Cercul giratoriu interior */}
            <circle cx={CX} cy={CY} r="40" fill="none" stroke="#333" strokeWidth="2"/>
            {/* Linie centrală giratoriu (întreruptă) */}
            <circle cx={CX} cy={CY} r="60" fill="none" stroke="#666" strokeWidth="1.5" strokeDasharray="10,8"/>
            {/* Săgeți direcție (sens orar) */}
            <polygon points={`${CX + 58},${CY - 20} ${CX + 53},${CY - 13} ${CX + 63},${CY - 13}`} fill="#333"/>
            <polygon points={`${CX - 58},${CY + 20} ${CX - 63},${CY + 13} ${CX - 53},${CY + 13}`} fill="#333"/>
            {/* Străzi de acces - verticale */}
            <line x1={CX - 15} y1={CY + 80} x2={CX - 15} y2={H} stroke="#333" strokeWidth="2"/>
            <line x1={CX + 15} y1={CY + 80} x2={CX + 15} y2={H} stroke="#333" strokeWidth="2"/>
            <line x1={CX - 15} y1="0" x2={CX - 15} y2={CY - 80} stroke="#333" strokeWidth="2"/>
            <line x1={CX + 15} y1="0" x2={CX + 15} y2={CY - 80} stroke="#333" strokeWidth="2"/>
            {/* Străzi de acces - orizontale */}
            <line x1="0" y1={CY - 15} x2={CX - 80} y2={CY - 15} stroke="#333" strokeWidth="2"/>
            <line x1="0" y1={CY + 15} x2={CX - 80} y2={CY + 15} stroke="#333" strokeWidth="2"/>
            <line x1={CX + 80} y1={CY - 15} x2={W} y2={CY - 15} stroke="#333" strokeWidth="2"/>
            <line x1={CX + 80} y1={CY + 15} x2={W} y2={CY + 15} stroke="#333" strokeWidth="2"/>
          </g>
        );
		
		case 'curba':
        {
          const curveOff = 60;
          return (
            <g>
              <path d={`M ${CX-45} ${H} Q ${CX-45-curveOff} ${CY} ${CX-45} 0 L ${CX+45} 0 Q ${CX+45-curveOff} ${CY} ${CX+45} ${H} Z`} fill="#f2f2f2" stroke="none"/>
              <path d={`M ${CX-45} ${H} Q ${CX-45-curveOff} ${CY} ${CX-45} 0`} fill="none" stroke="#333" strokeWidth="2"/>
              <path d={`M ${CX+45} ${H} Q ${CX+45-curveOff} ${CY} ${CX+45} 0`} fill="none" stroke="#333" strokeWidth="2"/>
              <path d={`M ${CX} ${H} Q ${CX-curveOff} ${CY} ${CX} 0`} fill="none" stroke="#666" strokeWidth="1.5" strokeDasharray="10,8"/>
            </g>
          );
        }
        
      case 'parcare':
        return (
          <g>
            {/* Contur zona de parcare */}
            <rect x="20" y={CY - 50} width={W - 40} height="100" fill="none" stroke="#333" strokeWidth="2"/>
            {/* Locuri de parcare */}
            {[60, 110, 160, 210, 260, 310].map(x => (
              <line key={x} x1={x} y1={CY - 50} x2={x} y2={CY + 50} stroke="#333" strokeWidth="1.5"/>
            ))}
            {/* Culoar de circulație */}
            <line x1="20" y1={CY + 55} x2={W - 20} y2={CY + 55} stroke="#333" strokeWidth="2"/>
            <line x1="20" y1={CY + 90} x2={W - 20} y2={CY + 90} stroke="#333" strokeWidth="2"/>
            <line x1="20" y1={CY + 72} x2={W - 20} y2={CY + 72} stroke="#666" strokeWidth="1.5" strokeDasharray="10,8"/>
            {/* Etichetă PARCARE */}
            <text x={CX} y={CY - 58} textAnchor="middle" fill="#333" fontSize="10" fontWeight="bold" fontFamily="Arial, sans-serif">PARCARE</text>
          </g>
        );
        
      default:
        return (
          <g>
            <line x1={CX - 45} y1="0" x2={CX - 45} y2={H} stroke="#333" strokeWidth="2"/>
            <line x1={CX + 45} y1="0" x2={CX + 45} y2={H} stroke="#333" strokeWidth="2"/>
            <line x1={CX} y1="0" x2={CX} y2={H} stroke="#666" strokeWidth="1.5" strokeDasharray="10,8"/>
          </g>
        );
    }
  };
  
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ background: '#ffffff', maxWidth: '100%', height: 'auto' }}>
      {/* Grid subtil (Varianta 2) */}
      <defs>
        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f0f0f0" strokeWidth="0.5"/>
        </pattern>
      </defs>
      <rect width={W} height={H} fill="url(#grid)"/>
      
      {/* Drumul */}
      {renderRoad()}
      
      {/* Numele scenariului */}
      {scenario && (
        <text x={CX} y="16" textAnchor="middle" fill="#333" fontSize="10" fontWeight="bold" fontFamily="Arial, sans-serif">
          {scenario.name}
        </text>
      )}
      
      {/* Numele străzii */}
      {data.numeStrada && (
        <text x={CX} y={scenario ? 28 : 16} textAnchor="middle" fill="#555" fontSize="9" fontFamily="Arial, sans-serif">
          {data.numeStrada}
        </text>
      )}
      
      {/* Mașinile */}
      <Car x={posA.x} y={posA.y} rotation={posA.rotation} filled={true} label="A" />
      <Car x={posB.x} y={posB.y} rotation={posB.rotation} filled={false} label="B" />
      
      {/* Punctul de impact */}
      <g transform={`translate(${impactPos.x}, ${impactPos.y})`}>
        <circle r="10" fill="none" stroke="#cc0000" strokeWidth="2"/>
        <line x1="-5" y1="-5" x2="5" y2="5" stroke="#cc0000" strokeWidth="2"/>
        <line x1="5" y1="-5" x2="-5" y2="5" stroke="#cc0000" strokeWidth="2"/>
      </g>
      
      {/* Legenda */}
      {showLegend && (
        <g>
          <rect x="5" y={H - 45} width="90" height="40" fill="#fff" rx="3" stroke="#ccc" strokeWidth="1"/>
          {/* Vehicul A - pătrat umplut */}
          <rect x="10" y={H - 38} width="14" height="10" rx="2" fill="#e0e0e0" stroke="#000" strokeWidth="1.5"/>
          <text x="28" y={H - 29} fontSize="9" fill="#333" fontFamily="Arial, sans-serif">Vehicul A</text>
          {/* Vehicul B - doar contur */}
          <rect x="10" y={H - 24} width="14" height="10" rx="2" fill="none" stroke="#000" strokeWidth="1.5"/>
          <text x="28" y={H - 15} fontSize="9" fill="#333" fontFamily="Arial, sans-serif">Vehicul B</text>
        </g>
      )}
      
      {/* Indicație */}
      <text x={W - 8} y={H - 6} textAnchor="end" fill="#aaa" fontSize="7" fontFamily="Arial, sans-serif">
        Schiță generată AI
      </text>
    </svg>
  );
});

// ========== PREVIEW SCENARIU (pentru selector) ==========
export const ScenarioPreview = memo(({ scenarioId, width = 120, height = 90 }) => {
  const scenario = ACCIDENT_SCENARIOS[scenarioId];
  if (!scenario) return null;
  
  return (
    <AccidentSketch 
      data={{ ...scenario, scenarioId }} 
      width={width} 
      height={height} 
      showLegend={false}
    />
  );
});

// ========== GRILĂ DESEN MANUAL ==========
export const ManualSketchGrid = memo(({ width = 380, height = 280 }) => {
  const cellSize = 20;
  const cols = Math.floor(width / cellSize);
  const rows = Math.floor(height / cellSize);
  
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ background: '#fff', maxWidth: '100%', height: 'auto' }}>
      {Array.from({ length: cols + 1 }).map((_, i) => (
        <line key={`v${i}`} x1={i * cellSize} y1="0" x2={i * cellSize} y2={height} stroke="#f0f0f0" strokeWidth="0.5"/>
      ))}
      {Array.from({ length: rows + 1 }).map((_, i) => (
        <line key={`h${i}`} x1="0" y1={i * cellSize} x2={width} y2={i * cellSize} stroke="#f0f0f0" strokeWidth="0.5"/>
      ))}
      <text x={width / 2} y={height / 2 - 10} textAnchor="middle" fill="#999" fontSize="13" fontFamily="Arial, sans-serif">
        Schița se completează manual pe hârtie
      </text>
      <text x={width / 2} y={height / 2 + 10} textAnchor="middle" fill="#999" fontSize="11" fontFamily="Arial, sans-serif">
        În PDF va apărea o grilă pentru desen
      </text>
    </svg>
  );
});

// ========== RADIO GROUP ==========
export const RadioGroup = memo(({ value, onChange }) => (
  <div className="radio-group">
    <button type="button" className={`radio-option ${value === 'da' ? 'selected' : ''}`} onClick={() => onChange('da')}>Da</button>
    <button type="button" className={`radio-option ${value === 'nu' ? 'selected' : ''}`} onClick={() => onChange('nu')}>Nu</button>
  </div>
));